import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import {
    Card,
    CardBody,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    NavItem,
    Row,
    TabContent,
    TabPane,
    UncontrolledDropdown,
} from 'reactstrap'
import AppPermissionsSwitcher from '../../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../../Components/AppPermissionsSwitcher/constants/actions'
import { EntityParamsProvider } from '../../../Components/AppPermissionsSwitcher/EntityParamsContext'
import BackButton from '../../../Components/BackButton/BackButton'
import CollapseCard from '../../../Components/Common/CollapseCard'
import FormTabNavigation from '../../../Components/Common/FormTabNavigation'
import Loader from '../../../Components/Common/Loader'
import NavigationActions from '../../../Components/Common/NavigationActions/NavigationActions'
import ViewDataCard from '../../../Components/Common/ViewDataCard'
import ViewDataList from '../../../Components/Common/ViewDataList'
import { DocumentTable } from '../../../Components/Documents'
import MapContainer from '../../../Components/MapContainer'
import { setupDeleteCallback } from '../../../Components/Modals/DeleteModal/utils'
import NotesViewAccordion from '../../../Components/NotesViewAccordion'
import QueryErrorBoundary from '../../../Components/QueryErrorBoundary/QueryErrorBoundary'
import Slider from '../../../Components/Slider/Slider'
import SliderOptions from '../../../Components/Slider/SliderOptions'
import { useColumnOrder } from '../../../Components/TanstackTable'
import images from '../../../api/images/hooks'
import projects from '../../../api/projects/hooks'
import { MODAL_TYPES } from '../../../common/constants'
import { projectResponseFields } from '../../../common/response/project'
import { propertyResponseFields } from '../../../common/response/property'
import { showModal } from '../../../store/modal'
import { selectSearchCriteriaProjects } from '../../../store/search'
import { coerceLatLng, embedYotubeVideo, isApproxLocationSyncType } from '../../../utils/general'
import OpportunityTable from '../../Properties/PropertyView/OpportunityList/OpportunityTable/OpportunityTable'
import useActivityColumns from '../../Properties/PropertyView/OpportunityList/OpportunityTable/TableColumns/useActivityColumns'
import { projectViewTabs } from '../constants'
import { useProjectGeneralViewData } from '../hooks'
import RelatedPropertiesViewTab from './RelatedPropertiesViewTab'
import { getModalPayloads } from './viewModalPayloads'

const ProjectView = () => {
    const { id } = useParams()

    return (
        <div className="page-content">
            <Container fluid>
                <QueryErrorBoundary resetKeys={[id]}>
                    <ProjectViewContent id={id} />
                </QueryErrorBoundary>
            </Container>
        </div>
    )
}

const ProjectViewContent = ({ id }) => {
    const { t } = useTranslation()
    const history = useHistory()
    const location = useLocation()
    const dispatch = useDispatch()
    const { OFFER_BASE_MODAL, DELETE_MODAL } = MODAL_TYPES

    const currentLang = localStorage.getItem('i18nextLng')

    const criteria = useSelector(selectSearchCriteriaProjects)
    const order = useColumnOrder('project')
    const params = { criteria, order }

    const { isLoading: fetchingProject, data: projectData } = projects.GetProject.useQuery(id, params, { throwOnError: true })
    const { mutate: deleteProject } = projects.DeleteProject.useMutation()

    const { isLoading: fetchingPhotos, data: photosData } = images.GetImages.useQuery({ module: 'project', parent: id, collection: 'photo' })
    const { isLoading: fetchingGroundPlans, data: groundPlansData } = images.GetImages.useQuery({
        module: 'project',
        parent: id,
        collection: 'ground_plan',
    })

    const project = projectData?.item ?? null
    const photos = !fetchingPhotos ? photosData : []
    const groundPlans = !fetchingGroundPlans ? groundPlansData : []

    const viewData = useProjectGeneralViewData(project)

    const activityColumns = useActivityColumns()

    const tabs = projectViewTabs()
    const [activeTab, setActiveTab] = useState(tabs[0].name)
    const [activeMaterialsTab, setActiveMaterialsTab] = useState('photos')

    useEffect(() => {
        if (fetchingPhotos || fetchingGroundPlans) return

        switch (true) {
            case Boolean(!photos.length && !groundPlans.length):
                if (project?.[projectResponseFields.MAP_LATITUDE] && project?.[projectResponseFields.MAP_LONGITUDE]) {
                    setActiveMaterialsTab('map')
                    return
                }

                if (project?.[projectResponseFields.VIRTUAL_WALK_URL]) {
                    setActiveMaterialsTab('3d')
                    return
                }

                if (project?.[projectResponseFields.PROPERTY_VIDEO_URL]) {
                    setActiveMaterialsTab('video')
                    return
                }
                break
            case Boolean(!photos.length && groundPlans.length):
                setActiveMaterialsTab('groundPlans')
                break
            default:
                setActiveMaterialsTab('photos')
                break
        }
    }, [fetchingPhotos, fetchingGroundPlans, project, photos.length, groundPlans.length])

    const handleTabChange = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab)
        }
    }

    const handleMaterialsTabChange = (tab) => {
        if (activeMaterialsTab !== tab) {
            setActiveMaterialsTab(tab)
        }
    }

    const handleModal = (modalType, actions = []) => {
        const payload = getModalPayloads(modalType, project, id)

        if (!payload) return

        setupDeleteCallback(modalType, payload.modalProps, (projectId) =>
            deleteProject({ id: projectId }, { onSuccess: () => history.push('/projects') })
        )

        actions.forEach((action) => dispatch(action(payload)))
    }

    if (fetchingProject) {
        return <Loader />
    }

    document.title = `${project?.translations[currentLang]?.projectTitle ?? t('app.common.projects')} | ${t('app.title')}`

    return (
        <EntityParamsProvider value={params}>
            <Row>
                <Col xs={12}>
                    <div className={`page-title-box d-sm-flex align-items-center mt-3 ps-0`}>
                        <BackButton className="me-3" />
                        <div>
                            <h4 className="mb-sm-0">{project?.translations[currentLang]?.projectTitle}</h4>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row className="d-flex justify-content-between align-items-center mb-3">
                <Col sm={12} md={8}>
                    <FormTabNavigation tabs={tabs} activeTab={activeTab} handleTabChange={handleTabChange} customClass="detail-view-tabs">
                        <NavItem>
                            <UncontrolledDropdown>
                                <DropdownToggle title={t('table.showMore')} href="#" className="real-estate-form-nav-link nav-link dropdown" tag="a">
                                    <i className="ri-more-fill align-middle"></i>
                                </DropdownToggle>
                                <DropdownMenu className="dropdown-menu-end">
                                    <AppPermissionsSwitcher permission="ModulePermissions" module={location} action={action.edit}>
                                        {({ hasModulePermission }) =>
                                            hasModulePermission && (
                                                <>
                                                    <DropdownItem onClick={() => history.push(`/projects/${id}/edit`)}>
                                                        <i className="mdi mdi-pencil-outline align-bottom me-2 text-muted"></i>{' '}
                                                        {t('view.project.action.edit')}
                                                    </DropdownItem>
                                                    <DropdownItem onClick={() => handleModal(DELETE_MODAL, [showModal])}>
                                                        <i className="mdi mdi-trash-can-outline align-bottom me-2 text-muted"></i>{' '}
                                                        {t('view.project.action.delete')}
                                                    </DropdownItem>
                                                </>
                                            )
                                        }
                                    </AppPermissionsSwitcher>
                                    <DropdownItem onClick={() => history.push('/projects/new')}>
                                        <i className="mdi mdi-plus align-bottom me-2 text-muted"></i> {t('view.project.action.add')}
                                    </DropdownItem>
                                    <DropdownItem onClick={() => handleModal(OFFER_BASE_MODAL, [showModal])}>
                                        <i className="mdi mdi-file-document-multiple-outline align-bottom me-2 text-muted"></i>{' '}
                                        {t('app.common.offerOrPrint')}
                                    </DropdownItem>
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        </NavItem>
                    </FormTabNavigation>
                </Col>
                <Col sm={12} md={4}>
                    <NavigationActions links={projectData.links} />
                </Col>
            </Row>

            <TabContent activeTab={activeTab} className="text-muted">
                <TabPane tabId="about">
                    <Row>
                        <Col sm={12} xxl={3}>
                            {viewData?.general && viewData.general.some((x) => x.value) && <ViewDataCard dataList={viewData.general} />}
                            {viewData?.location && viewData.location.some((x) => x.value) && <ViewDataCard dataList={viewData.location} />}
                            {viewData?.owners && viewData.owners.some((x) => x.value) && (
                                <CollapseCard header={t('app.common.owner')} data={viewData.owners}>
                                    <ViewDataList dataList={viewData.owners} />
                                </CollapseCard>
                            )}
                            <ViewDataCard dataList={viewData?.other} />
                        </Col>

                        <Col sm={12} xxl={9}>
                            <Card>
                                <CardBody>
                                    <Row>
                                        <Col sm={12} xl={6}>
                                            {viewData?.price?.map((viewItem, index) => {
                                                if (viewItem.value) {
                                                    return (
                                                        <Row
                                                            className={`align-items-start ${viewData.price.length - 1 !== index ? 'mb-4' : ''}`}
                                                            key={viewItem?.id}
                                                        >
                                                            <Col sm={6}>
                                                                <span className="flex-grow-1 fw-medium">{viewItem?.label}:</span>
                                                            </Col>
                                                            <Col sm={6}>
                                                                <span className="flex-grow-1 fw-light">
                                                                    {!viewItem?.value?.length ? viewItem?.value : [...viewItem.value]}
                                                                </span>
                                                            </Col>
                                                        </Row>
                                                    )
                                                }
                                                return null
                                            })}
                                            {viewData?.price?.some((x) => x.value) && <div className="my-4"></div>}
                                            {viewData?.details?.map((viewItem, index) => {
                                                if (viewItem.value) {
                                                    return (
                                                        <Row
                                                            className={`align-items-start ${viewData.details.length - 1 !== index ? 'mb-4' : ''}`}
                                                            key={viewItem?.id}
                                                        >
                                                            <Col sm={6}>
                                                                <span className="flex-grow-1 fw-medium">{viewItem?.label}:</span>
                                                            </Col>
                                                            <Col sm={6}>
                                                                <span className="flex-grow-1 fw-light">
                                                                    {!viewItem?.value?.length ? viewItem?.value : [...viewItem.value]}
                                                                </span>
                                                            </Col>
                                                        </Row>
                                                    )
                                                }
                                                return null
                                            })}
                                        </Col>
                                        <Col sm={12} xl={6}>
                                            <Row className="mb-4">
                                                <Col>
                                                    {fetchingPhotos || fetchingGroundPlans ? (
                                                        <div className="d-flex align-items-center justify-content-center" style={{ height: '370px' }}>
                                                            <Loader />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <TabContent activeTab={activeMaterialsTab}>
                                                                {photos?.length ? (
                                                                    <TabPane tabId="photos">
                                                                        <Slider module={location} imageList={photos} />
                                                                    </TabPane>
                                                                ) : null}
                                                                {groundPlans?.length ? (
                                                                    <TabPane tabId="groundPlans">
                                                                        <Slider module={location} imageList={groundPlans} />
                                                                    </TabPane>
                                                                ) : null}
                                                                {project?.[propertyResponseFields.VIRTUAL_WALK_URL] ? (
                                                                    <TabPane tabId="3d">
                                                                        <div className="ratio ratio-4x3">
                                                                            <iframe
                                                                                title="3D virtualna šetnja"
                                                                                src={project[propertyResponseFields.VIRTUAL_WALK_URL]}
                                                                            />
                                                                        </div>
                                                                    </TabPane>
                                                                ) : null}
                                                                {project?.[propertyResponseFields.PROPERTY_VIDEO_URL] ? (
                                                                    <TabPane tabId="video">
                                                                        <div className="ratio ratio-4x3">
                                                                            <iframe
                                                                                title="Youtube video"
                                                                                src={embedYotubeVideo(
                                                                                    project[propertyResponseFields.PROPERTY_VIDEO_URL]
                                                                                )}
                                                                            />
                                                                        </div>
                                                                    </TabPane>
                                                                ) : null}
                                                                {project?.[projectResponseFields.MAP_LATITUDE] &&
                                                                project?.[projectResponseFields.MAP_LONGITUDE] ? (
                                                                    <AppPermissionsSwitcher permission="GoogleMaps" module={location}>
                                                                        {({ showMapsSlider }) =>
                                                                            showMapsSlider ? (
                                                                                <TabPane tabId="map">
                                                                                    <MapContainer
                                                                                        marker={coerceLatLng(project)}
                                                                                        showRadius={isApproxLocationSyncType(project)}
                                                                                        height="399px"
                                                                                    />
                                                                                </TabPane>
                                                                            ) : null
                                                                        }
                                                                    </AppPermissionsSwitcher>
                                                                ) : null}
                                                            </TabContent>

                                                            <AppPermissionsSwitcher
                                                                permission="AllowedPhotos"
                                                                module={location}
                                                                photos={photos}
                                                                groundPlans={groundPlans}
                                                            >
                                                                {({ photos, groundPlans }) =>
                                                                    photos.length || groundPlans.length ? (
                                                                        <SliderOptions
                                                                            photos={photos}
                                                                            groundPlans={groundPlans}
                                                                            activeMaterialsTab={activeMaterialsTab}
                                                                            handleMaterialsTabChange={handleMaterialsTabChange}
                                                                            viewData={project}
                                                                        />
                                                                    ) : null
                                                                }
                                                            </AppPermissionsSwitcher>
                                                        </>
                                                    )}
                                                </Col>
                                            </Row>
                                            {viewData?.relatedData?.map((viewItem, index) => {
                                                if (viewItem.value) {
                                                    return (
                                                        <Row
                                                            className={`align-items-start ${viewData.details.length - 1 !== index ? 'mb-4' : ''}`}
                                                            key={viewItem?.id}
                                                        >
                                                            <Col sm={6}>
                                                                <span className="flex-grow-1 fw-medium">{viewItem?.label}:</span>
                                                            </Col>
                                                            <Col sm={6}>
                                                                <span className="flex-grow-1 fw-light">
                                                                    {!viewItem?.value?.length ? viewItem?.value : [...viewItem.value]}
                                                                </span>
                                                            </Col>
                                                        </Row>
                                                    )
                                                }
                                                return null
                                            })}
                                        </Col>
                                    </Row>
                                    {project?.translations[currentLang]?.projectTitle && (
                                        <Row className="align-items-center mt-5">
                                            <Col sm={3}>
                                                <span className="flex-grow-1 fw-medium">{t('app.common.title')}:</span>
                                            </Col>
                                            <Col sm={9}>
                                                <span className="flex-grow-1 fw-light">{project?.translations[currentLang]?.projectTitle}</span>
                                            </Col>
                                        </Row>
                                    )}
                                    {project?.translations[currentLang]?.projectDescription && (
                                        <Row className="mt-5">
                                            <Col sm={12} className="mb-3">
                                                <span className="flex-grow-1 fw-medium">{t('form.project.description.webField.label')}:</span>
                                            </Col>
                                            <Col sm={12} className="fw-light text-secondary">
                                                {
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: project?.translations[currentLang]?.projectDescription,
                                                        }}
                                                    />
                                                }
                                            </Col>
                                        </Row>
                                    )}
                                    {project?.translations[currentLang]?.projectOfferDescription && (
                                        <Row className="mt-5">
                                            <Col sm={12} className="mb-3">
                                                <span className="flex-grow-1 fw-medium">{t('form.project.description.offerField.label')}:</span>
                                            </Col>
                                            <Col sm={12} className="fw-light text-secondary">
                                                {
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: project?.translations[currentLang]?.projectOfferDescription,
                                                        }}
                                                    />
                                                }
                                            </Col>
                                        </Row>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </TabPane>
                {activeTab === 'notes' && (
                    <TabPane tabId="notes">
                        <NotesViewAccordion entityId={id} noteEntity={'project'} />
                    </TabPane>
                )}
                {activeTab === 'documents' && (
                    <TabPane tabId="documents">
                        <Card>
                            <CardBody>
                                <DocumentTable
                                    parentId={id}
                                    documentModule="project"
                                    onAddClick={() => {
                                        history.push({
                                            pathname: `/projects/${id}/edit`,
                                            state: { activeTab: 'documents' },
                                        })
                                    }}
                                />
                            </CardBody>
                        </Card>
                    </TabPane>
                )}
                {activeTab === 'relatedProperties' && (
                    <TabPane tabId="relatedProperties">
                        <Card>
                            <CardBody>
                                <RelatedPropertiesViewTab projectId={id} />
                            </CardBody>
                        </Card>
                    </TabPane>
                )}
                {activeTab === 'activities' && (
                    <TabPane tabId="activities">
                        <Card>
                            <CardBody>
                                <OpportunityTable columns={activityColumns} url="activity" tableKey="opportunityActivityTable" entityType="project" />
                            </CardBody>
                        </Card>
                    </TabPane>
                )}
            </TabContent>
        </EntityParamsProvider>
    )
}

export default ProjectView
